import axios from "axios";

/**
 * API base URL resolution — environment-agnostic.
 *
 * We deliberately DO NOT embed an absolute backend URL at build time. Instead
 * every call goes to a relative `/api/...` path, which means the browser
 * targets whatever origin currently serves the app:
 *
 *   - Preview (png-payroll-app.preview.emergentagent.com): the platform ingress
 *     routes `/api/*` → backend container. Same origin. Works.
 *   - Production (payroll.kalmarpacific.com or any other domain): the operator's
 *     nginx routes `/api/*` → backend container. Same origin. Works.
 *
 * Same-origin also means: no CORS to configure, no `Authorization` header lost
 * to a preflight, no cookie-domain drama if we ever move off Bearer tokens.
 *
 * Legacy escape hatch: if REACT_APP_BACKEND_URL is *explicitly* set to a value
 * different from window.location.origin (e.g. dev-server on port 3000 calling
 * backend on 8001), fall back to that absolute URL. In every deploy path we
 * care about, this branch is skipped.
 */
function resolveApiBase() {
    const envUrl = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
    if (typeof window !== "undefined" && envUrl && envUrl !== window.location.origin) {
        return `${envUrl}/api`;
    }
    return "/api";
}

const BASE = resolveApiBase();

// 30s ceiling — anything slower is almost always a proxy/CF challenge stuck in the middle,
// and it's better to surface a real error than to leave the user staring at "Signing in…".
const api = axios.create({ baseURL: BASE, timeout: 30000 });

api.interceptors.request.use((config) => {
    const token = localStorage.getItem("mss_token");
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

api.interceptors.response.use(
    (r) => r,
    (err) => {
        const status = err?.response?.status;
        if (status === 401 && typeof window !== "undefined") {
            const path = window.location.pathname;
            if (path !== "/login") {
                localStorage.removeItem("mss_token");
                window.location.href = "/login";
            }
        }
        return Promise.reject(err);
    },
);

export function formatApiError(detail) {
    if (detail == null) return "Something went wrong. Please try again.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
        return detail
            .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
            .filter(Boolean)
            .join(" ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
}

/**
 * Turn an axios failure into a user-actionable message.
 *
 * Distinguishes:
 *   - a real server response with a `detail` payload (e.g. 401 "invalid credentials")
 *   - a network-level failure where no response ever arrived (typical shapes:
 *     axios timeout, DNS failure, ad-blocker abort, Cloudflare JS-challenge
 *     stuck mid-flight, corporate proxy dropping the connection).
 *
 * The generic "Something went wrong" copy is useless for real users — this
 * helper surfaces the actual failure mode so the user knows what to try.
 */
export function loginErrorMessage(ex) {
    // 1. Server responded with something → show the server's message.
    if (ex?.response) {
        return formatApiError(ex.response?.data?.detail) || "Login failed";
    }
    // 2. Axios timeout — the 30s ceiling.
    if (ex?.code === "ECONNABORTED") {
        return "The server didn't respond in 30 seconds. Check your network, disable any ad-blocker or VPN, and try again — or try an incognito window.";
    }
    // 3. Anything else with no response = the browser never reached the server.
    return "Can't reach the server. This is usually an ad-blocker, VPN, corporate proxy, or a Cloudflare bot challenge blocking the request. Try an incognito window or a different network.";
}

export default api;
